import { create } from 'zustand';
import { hashFile } from '../lib/hash.ts';
import { commitFile, probeBlocks, sliceBlocks, uploadBlock } from '../api/upload.ts';
import { joinPath } from '../lib/format.ts';
import { ApiError } from '../api/http.ts';

export type UploadState =
  | 'queued' | 'hashing' | 'uploading' | 'committing' | 'done' | 'error' | 'canceled';

export interface UploadItem {
  id: string;
  file: File;
  name: string;
  targetPath: string;
  namespaceId: string;
  size: number;
  state: UploadState;
  /** 0..1 */
  progress: number;
  uploadedBytes: number;
  plannedBytes: number;
  error: string | null;
  startedAt: number;
  controller: AbortController;
}

const MAX_PARALLEL_FILES = 3;
const MAX_PARALLEL_BLOCKS = 4;

interface UploadsState {
  items: UploadItem[];
  open: boolean;
  running: number;
  onFinished: ((namespaceId: string) => void) | null;
  enqueue: (files: File[], namespaceId: string, folder: string) => void;
  enqueueEntries: (entries: Array<{ file: File; relativePath: string }>,
                   namespaceId: string, folder: string) => void;
  cancel: (id: string) => void;
  retry: (id: string) => void;
  clearFinished: () => void;
  setOpen: (open: boolean) => void;
  setOnFinished: (fn: (namespaceId: string) => void) => void;
}

let seq = 0;

export const useUploads = create<UploadsState>((set, get) => ({
  items: [],
  open: false,
  running: 0,
  onFinished: null,

  enqueue: (files, namespaceId, folder) => {
    get().enqueueEntries(
      files.map((file) => ({ file, relativePath: file.name })), namespaceId, folder,
    );
  },

  enqueueEntries: (entries, namespaceId, folder) => {
    const items = entries.map(({ file, relativePath }): UploadItem => ({
      id: `u${++seq}`,
      file,
      name: relativePath,
      targetPath: joinPath(folder, relativePath.split('/').map((s) => s).join('/')),
      namespaceId,
      size: file.size,
      state: 'queued',
      progress: 0,
      uploadedBytes: 0,
      plannedBytes: file.size,
      error: null,
      startedAt: Date.now(),
      controller: new AbortController(),
    }));

    set({ items: [...get().items, ...items], open: true });
    pump(set, get);
  },

  cancel: (id) => {
    const item = get().items.find((i) => i.id === id);
    item?.controller.abort();
    patch(set, get, id, { state: 'canceled' });
  },

  retry: (id) => {
    patch(set, get, id, {
      state: 'queued', error: null, progress: 0, uploadedBytes: 0,
      controller: new AbortController(),
    });
    pump(set, get);
  },

  clearFinished: () => {
    set({
      items: get().items.filter(
        (i) => i.state !== 'done' && i.state !== 'canceled' && i.state !== 'error',
      ),
    });
  },

  setOpen: (open) => { set({ open }); },
  setOnFinished: (fn) => { set({ onFinished: fn }); },
}));

type Setter = (partial: Partial<UploadsState>) => void;
type Getter = () => UploadsState;

function patch(set: Setter, get: Getter, id: string, changes: Partial<UploadItem>): void {
  set({ items: get().items.map((i) => (i.id === id ? { ...i, ...changes } : i)) });
}

/** 대기열에서 동시 실행 한도만큼 꺼내 실행한다. */
function pump(set: Setter, get: Getter): void {
  while (get().running < MAX_PARALLEL_FILES) {
    const next = get().items.find((i) => i.state === 'queued');
    if (!next) return;
    set({ running: get().running + 1 });
    patch(set, get, next.id, { state: 'hashing' });
    void runOne(set, get, next.id).finally(() => {
      set({ running: Math.max(0, get().running - 1) });
      pump(set, get);
    });
  }
}

async function runOne(set: Setter, get: Getter, id: string): Promise<void> {
  const item = get().items.find((i) => i.id === id);
  if (!item || item.state === 'canceled') return;

  try {
    const { hashes, contentHash } = await hashFile(item.file, (ratio) => {
      // 해시 단계는 전체 진행률의 앞 15% 를 차지한다
      patch(set, get, id, { progress: ratio * 0.15 });
    });
    if (get().items.find((i) => i.id === id)?.state === 'canceled') return;

    const probe = hashes.length > 0
      ? await probeBlocks(hashes)
      : { missing: [] as string[], have: 0, quota: { used: 0, quota: 0, available: 0 } };

    const parts = sliceBlocks(item.file);
    const missing = new Set(probe.missing);
    const toUpload = hashes
      .map((hash, index) => ({ hash, blob: parts[index]! }))
      .filter((b, index, all) =>
        missing.has(b.hash) && all.findIndex((x) => x.hash === b.hash) === index);

    const plannedBytes = toUpload.reduce((sum, b) => sum + b.blob.size, 0);
    patch(set, get, id, { state: 'uploading', plannedBytes, progress: 0.15 });

    await uploadAll(set, get, id, toUpload, plannedBytes, item.controller.signal);
    if (get().items.find((i) => i.id === id)?.state === 'canceled') return;

    patch(set, get, id, { state: 'committing', progress: 0.97 });
    await commitFile({
      namespace_id: item.namespaceId,
      path: item.targetPath,
      blocks: hashes,
      size: item.size,
      content_hash: contentHash,
      client_mtime: new Date(item.file.lastModified).toISOString(),
      parent_rev: null,
      // 같은 경로에 이미 파일이 있으면 덮지 않고 사본으로 저장한다
      autorename: true,
    });

    patch(set, get, id, { state: 'done', progress: 1 });
    get().onFinished?.(item.namespaceId);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      patch(set, get, id, { state: 'canceled' });
      return;
    }
    const message = err instanceof ApiError || err instanceof Error
      ? err.message : '업로드에 실패했습니다';
    patch(set, get, id, { state: 'error', error: message });
  }
}

async function uploadAll(
  set: Setter, get: Getter, id: string,
  blocks: Array<{ hash: string; blob: Blob }>, plannedBytes: number, signal: AbortSignal,
): Promise<void> {
  let uploaded = 0;
  let cursor = 0;

  const worker = async (): Promise<void> => {
    while (cursor < blocks.length) {
      const index = cursor++;
      const block = blocks[index]!;
      await uploadBlock(block.hash, block.blob, {
        signal,
        onProgress: (delta) => {
          uploaded += delta;
          const ratio = plannedBytes === 0 ? 1 : Math.min(1, uploaded / plannedBytes);
          patch(set, get, id, {
            uploadedBytes: uploaded,
            progress: 0.15 + ratio * 0.82,
          });
        },
      });
    }
  };

  const lanes = Math.min(MAX_PARALLEL_BLOCKS, Math.max(1, blocks.length));
  await Promise.all(Array.from({ length: lanes }, worker));
}

export function uploadStats(items: UploadItem[]): {
  active: number; done: number; failed: number; total: number; progress: number;
} {
  const active = items.filter(
    (i) => i.state === 'queued' || i.state === 'hashing'
      || i.state === 'uploading' || i.state === 'committing',
  ).length;
  const done = items.filter((i) => i.state === 'done').length;
  const failed = items.filter((i) => i.state === 'error').length;
  const totalBytes = items.reduce((s, i) => s + i.size, 0);
  const doneBytes = items.reduce((s, i) => s + i.size * i.progress, 0);
  return {
    active, done, failed, total: items.length,
    progress: totalBytes === 0 ? 0 : doneBytes / totalBytes,
  };
}
